import { FC, HTMLAttributes, memo, ReactNode } from 'react';

import { Conflict } from '@/types/Conflicten';
import { Defect } from '@/types/Defecten';
import usePlankTableStore from '@/views/ddp/hooks/usePlankTableStore';
import usePostNotitie from '@/views/ddp/hooks/usePostNotitie';
import useTable from '@/views/ddp/hooks/useTable';
import useTooltipVoorraad from '@/views/ddp/hooks/useTooltipVoorraad';
import { AtomicRBCMaterieelMap } from '@/views/ddp/state/types/VoorraadbeheerStore';
import { Reserve } from '@/views/ddp/types';
import { adjustMatTypeLength } from '@/views/ddp/utils/table';
import { getClaimsTotaal } from '@/views/ddp/utils/voorraadbeheer';

import {
  conflictToSelectionItem,
  defectToSelectionItem,
  reserveToSelectionItem,
} from '../../../utils/plankSelectionItems';
import ConflictListItem from '../../conflict/ConflictListItem';
import DefectListItem from '../../defect/DefectListItem';
import ReserveListItem from '../../reserve/ReserveListItem';
import VoorraadListItem from '../../VoorraadBeheer/VoorraadListItem';
import VoorraadTotaalAantal from '../../VoorraadBeheer/VoorraadTotaalAantal';
import TableList, { TableListType } from '../TableList/TableList';

import OnderhoudDropzone from './OnderhoudDropzone';

import plankStyles from '../../../PlankView.module.css';
import styles from '../Table.module.css';

type TableRowType = HTMLAttributes<HTMLDivElement> & {
  matType: string;
};

const ConflictList = memo((props: TableListType<Conflict>) => (
  <TableList {...props} toSelectionItem={conflictToSelectionItem} />
));
const ConflictRow: (conflict: Conflict) => ReactNode = (conflict: Conflict) => <ConflictListItem conflict={conflict} />;

const DefectList = memo((props: TableListType<Defect>) => (
  <TableList {...props} toSelectionItem={defectToSelectionItem} />
));
const DefectRow: (defect: Defect) => ReactNode = (defect: Defect) => <DefectListItem defect={defect} />;
const OnderhoudsRow: (defect: Defect) => ReactNode = (defect: Defect) => (
  <DefectListItem isOnderhoudHerstel defect={defect} />
);

const ReserveList = memo((props: TableListType<Reserve>) => (
  <TableList {...props} toSelectionItem={reserveToSelectionItem} />
));
const ReserveRow: (reserve: Reserve) => ReactNode = (reserve: Reserve) => <ReserveListItem reserve={reserve} />;

const VoorraadList = memo<TableListType<AtomicRBCMaterieelMap>>(TableList);
const VoorraadRow: (voorraadRBC: AtomicRBCMaterieelMap) => ReactNode = (voorraadRBC: AtomicRBCMaterieelMap) => (
  <VoorraadListItem voorraad={voorraadRBC} />
);
const TableRow: FC<TableRowType> = ({ matType }) => {
  const { defectNaarOnderhoud } = usePostNotitie();
  const isRowCollapsed = usePlankTableStore((state) => state.isRowCollapsed(matType));
  const toggleCollapseRow = usePlankTableStore((state) => state.toggleCollapseRow);

  const showColumn = usePlankTableStore((state) => state.showColumn);

  const {
    mancos,
    verkeerdMat,
    defectenInzetbaar,
    defectenBeperktInzetbaar,
    defectenNietInzetbaar,
    defectenInOnderhoud,
    reserves,
    voorraadMaterieel,
  } = useTable(matType);

  const { showTooltip, updateTooltip, hideTooltip } = useTooltipVoorraad();
  return (
    <div className={styles.ddpDataRowStyle}>
      <div
        className={`row-first${adjustMatTypeLength(matType) === matType ? '' : '-wrap'}`}
        style={{
          position: 'sticky',
          backgroundColor: 'var(--tractie-color-background-default)',
          padding: '0 24px',
          left: 0,
          zIndex: 21,
        }}
      >
        <h2 className={plankStyles.plankH2}>{adjustMatTypeLength(matType)}</h2>
        <a
          onMouseEnter={showTooltip(matType, voorraadMaterieel)}
          onMouseMove={updateTooltip(matType, voorraadMaterieel)}
          onMouseLeave={hideTooltip}
          style={{ textDecoration: 'none' }}
        >
          <VoorraadTotaalAantal
            aantal={voorraadMaterieel.overOfTekort}
            nvt={voorraadMaterieel.nvt}
            claimsCount={getClaimsTotaal(voorraadMaterieel.claims)}
            beschikbareReizigersdienst={
              voorraadMaterieel.aanwezig - voorraadMaterieel.inzetten - voorraadMaterieel.nietInzetbaar
            }
          />
        </a>
      </div>
      <div className={styles.ddpDataRowInnerStyle}>
        <div className={styles.tableRowBaseStyle}>
          {showColumn('Voorraadbeheer') && (
            <VoorraadList
              data={voorraadMaterieel.rbcList}
              renderRow={VoorraadRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRow(matType)}
              itemHeight={32}
            />
          )}

          {showColumn('Manco') && (
            <ConflictList
              data={mancos}
              renderRow={ConflictRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRow(matType)}
            />
          )}
          {showColumn('Reserve') && (
            <ReserveList
              dynamic
              data={reserves}
              estimateSize={
                // Size is = (32 sm/58 xl) + 2
                (index: number) => (reserves[index].buitenDienstTijd ? 60 : 34)
              }
              renderRow={ReserveRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRow(matType)}
            />
          )}
          {showColumn('Verkeerd mat') && (
            <ConflictList
              data={verkeerdMat}
              renderRow={ConflictRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRow(matType)}
            />
          )}
          {showColumn('Defect inzetbaar') && (
            <DefectList
              isDefect
              data-testid={`${matType}_defect_inzetbaar_data_list`}
              data={defectenInzetbaar}
              renderRow={DefectRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRow(matType)}
              totalTestId={`${matType}_defect_inzetbaar_data_list_total${isRowCollapsed ? '_collapsed' : ''}`}
            />
          )}
          {showColumn('Defect beperkt inzetbaar') && (
            <DefectList
              isDefect
              data-testid={`${matType}_defect_beperkt_inzetbaar_data_list`}
              data={defectenBeperktInzetbaar}
              renderRow={DefectRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRow(matType)}
              totalTestId={`${matType}_defect_beperkt_inzetbaar_data_list_total${isRowCollapsed ? '_collapsed' : ''}`}
            />
          )}
          {showColumn('Defect niet inzetbaar') && (
            <DefectList
              isDefect
              data={defectenNietInzetbaar}
              renderRow={DefectRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRow(matType)}
              totalTestId={`${matType}_defect_niet_inzetbaar_data_list_total${isRowCollapsed ? '_collapsed' : ''}`}
            />
          )}
          {showColumn('Onderhoud & herstel') && (
            <OnderhoudDropzone matType={matType} onDrop={defectNaarOnderhoud}>
              <DefectList
                isDefect
                data={defectenInOnderhoud}
                renderRow={OnderhoudsRow}
                collapse={isRowCollapsed}
                onTotalClick={() => toggleCollapseRow(matType)}
              />
            </OnderhoudDropzone>
          )}
        </div>
      </div>
    </div>
  );
};
export default TableRow;
